import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, PageHeader, Pill, dOnly, dt } from "./_shared";
import { Search } from "lucide-react";

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [status, setStatus] = useState("");
  const [plan, setPlan] = useState("");
  const [detail, setDetail] = useState(null);
  const [blockReason, setBlockReason] = useState("");

  const load = () => {
    const p = new URLSearchParams({ page, page_size: 20 });
    if (q) p.set("q", q);
    if (state) p.set("state", state);
    if (status) p.set("status", status);
    if (plan) p.set("plan", plan);
    api.get(`/admin/users?${p}`).then(r => { setRows(r.data.rows); setTotal(r.data.total); });
  };
  useEffect(() => { load(); }, [page, state, status, plan]);
  const pages = useMemo(() => Math.max(1, Math.ceil(total / 20)), [total]);

  const openDetail = async (uid) => {
    const r = await api.get(`/admin/users/${uid}`);
    setDetail(r.data);
    setBlockReason("");
  };

  const toggleBlock = async (blocked) => {
    if (!detail) return;
    if (blocked && !blockReason.trim()) { alert("Informe o motivo do bloqueio."); return; }
    await api.post(`/admin/users/${detail.user.user_id}/block`, { blocked, reason: blockReason });
    await openDetail(detail.user.user_id);
    load();
  };

  return (
    <div className="p-3 sm:p-6 lg:p-8" data-testid="admin-users">
      <PageHeader title="Usuários" subtitle={`${total} usuário(s) cadastrado(s)`} />

      <Card className="mb-6" testid="admin-users-filters">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==='Enter'&& (setPage(1), load())}
              placeholder="Buscar por nome, e-mail ou ID…"
              className="w-full pl-9 pr-3 py-2 bg-[#0f1524] border border-white/5 rounded-lg text-sm text-slate-200 placeholder-slate-500"
              data-testid="admin-users-search" />
          </div>
          <select value={state} onChange={e=>{setState(e.target.value); setPage(1);}}
            className="bg-[#0f1524] border border-white/5 rounded-lg text-sm px-3 py-2 text-slate-200"
            data-testid="admin-users-filter-state">
            <option value="">Todos estados</option>
            {UFS.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
          <select value={status} onChange={e=>{setStatus(e.target.value); setPage(1);}}
            className="bg-[#0f1524] border border-white/5 rounded-lg text-sm px-3 py-2 text-slate-200"
            data-testid="admin-users-filter-status">
            <option value="">Todos status</option>
            <option value="active">Ativos</option>
            <option value="blocked">Bloqueados</option>
          </select>
          <select value={plan} onChange={e=>{setPlan(e.target.value); setPage(1);}}
            className="bg-[#0f1524] border border-white/5 rounded-lg text-sm px-3 py-2 text-slate-200"
            data-testid="admin-users-filter-plan">
            <option value="">Todos planos</option>
            <option value="free">Free</option>
            <option value="pro">Pro</option>
            <option value="premium">Premium</option>
          </select>
          <button onClick={()=>{setPage(1); load();}}
            className="px-4 py-2 rounded-lg bg-[#d4a24c] text-black text-sm font-semibold"
            data-testid="admin-users-apply">
            Filtrar
          </button>
        </div>
      </Card>

      <Card testid="admin-users-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-white/5">
                <th className="py-3 pr-4">Usuário</th>
                <th className="py-3 pr-4">E-mail</th>
                <th className="py-3 pr-4">Cidade</th>
                <th className="py-3 pr-4">UF</th>
                <th className="py-3 pr-4">Plano</th>
                <th className="py-3 pr-4">Status</th>
                <th className="py-3 pr-4">Cadastro</th>
                <th className="py-3 pr-4">Últ. acesso</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan="8" className="py-8 text-center text-slate-500">Sem resultados</td></tr>
              )}
              {rows.map(r => (
                <tr key={r.user_id}
                  onClick={()=>openDetail(r.user_id)}
                  className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer"
                  data-testid={`admin-user-row-${r.user_id}`}
                >
                  <td className="py-3 pr-4">
                    <div className="text-slate-100">{r.treatment ? `${r.treatment} ` : ""}{r.name}</div>
                    <div className="text-[11px] text-slate-500">{r.user_id}</div>
                  </td>
                  <td className="py-3 pr-4 text-slate-300">{r.email}</td>
                  <td className="py-3 pr-4 text-slate-300">{r.city || "—"}</td>
                  <td className="py-3 pr-4 text-slate-300">{r.state_code || "—"}</td>
                  <td className="py-3 pr-4"><Pill tone={r.plan === "free" ? "slate" : "gold"}>{r.plan}</Pill></td>
                  <td className="py-3 pr-4"><Pill tone={r.status === "blocked" ? "red" : "green"}>{r.status}</Pill></td>
                  <td className="py-3 pr-4 text-slate-400">{dOnly(r.created_at)}</td>
                  <td className="py-3 pr-4 text-slate-400">{r.last_access ? dt(r.last_access) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-4">
          <div className="text-xs text-slate-500">Página {page} / {pages}</div>
          <div className="flex gap-2">
            <button disabled={page<=1} onClick={()=>setPage(p=>p-1)}
              className="px-3 py-1.5 rounded-lg bg-[#0f1524] border border-white/5 text-slate-200 disabled:opacity-40" data-testid="admin-users-prev">Anterior</button>
            <button disabled={page>=pages} onClick={()=>setPage(p=>p+1)}
              className="px-3 py-1.5 rounded-lg bg-[#0f1524] border border-white/5 text-slate-200 disabled:opacity-40" data-testid="admin-users-next">Próxima</button>
          </div>
        </div>
      </Card>

      {detail && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={()=>setDetail(null)} role="dialog" aria-modal="true" data-testid="admin-user-detail-modal">
          <div className="max-w-3xl w-full bg-[#0f1524] border border-white/10 rounded-2xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()} data-testid="admin-user-detail">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#d4a24c]/80">Ficha do usuário</div>
                <h2 className="text-2xl font-serif text-slate-100">{detail.user.treatment ? `${detail.user.treatment} ` : ""}{detail.user.name}</h2>
                <div className="text-sm text-slate-400">{detail.user.email}</div>
              </div>
              <button onClick={()=>setDetail(null)} className="text-slate-500 hover:text-slate-200">✕</button>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Identificação</div>
                <div>ID: <span className="text-slate-300">{detail.user.user_id}</span></div>
                <div>Telefone: <span className="text-slate-300">{detail.lawyer?.telefone || "—"}</span></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Dados pessoais</div>
                <div>Nascimento: <span className="text-slate-300">{detail.user.birth_date ? dOnly(detail.user.birth_date) : "—"}</span></div>
                <div>Idade: <span className="text-slate-300">{detail.user.age ?? "—"}</span></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Localização</div>
                <div>Cidade: <span className="text-slate-300">{detail.user.city || "—"}</span></div>
                <div>Estado: <span className="text-slate-300">{detail.user.state_name || detail.user.state_code || "—"}</span></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Conta</div>
                <div>Cadastro: <span className="text-slate-300">{dOnly(detail.user.created_at)}</span></div>
                <div>Últ. acesso: <span className="text-slate-300">{detail.user.last_access ? dt(detail.user.last_access) : "—"}</span></div>
                <div>Status: <Pill tone={detail.user.status === "blocked" ? "red" : "green"}>{detail.user.status || "active"}</Pill></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Uso</div>
                <div>Clientes: <span className="text-slate-300">{detail.counts.clients}</span></div>
                <div>Documentos: <span className="text-slate-300">{detail.counts.documents}</span></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Assinatura</div>
                <div>Plano: <Pill tone="gold">{detail.user.plan || "free"}</Pill></div>
              </div>
            </div>

            <div className="mt-6 border-t border-white/5 pt-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Ações</div>
              {detail.user.status === "blocked" ? (
                <button onClick={()=>toggleBlock(false)}
                  className="px-4 py-2 rounded-lg bg-emerald-600/80 text-white text-sm" data-testid="admin-unblock-btn">
                  Desbloquear usuário
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <input value={blockReason} onChange={e=>setBlockReason(e.target.value)} placeholder="Motivo do bloqueio…"
                    className="flex-1 bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200"
                    data-testid="admin-block-reason" />
                  <button onClick={()=>toggleBlock(true)}
                    className="px-4 py-2 rounded-lg bg-rose-600/80 text-white text-sm" data-testid="admin-block-btn">
                    Bloquear
                  </button>
                </div>
              )}
            </div>

            {detail.payments?.length > 0 && (
              <div className="mt-6">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Últimos pagamentos</div>
                <div className="max-h-56 overflow-y-auto text-xs">
                  {detail.payments.slice(0,10).map((p,i)=>(
                    <div key={i} className="flex justify-between border-b border-white/5 py-1.5">
                      <span className="text-slate-400">{dOnly(p.created_at)}</span>
                      <span className="text-slate-200">R$ {((p.amount||0)/100).toFixed(2)}</span>
                      <Pill tone={p.payment_status === "paid" ? "green" : p.payment_status === "pending" ? "amber" : "slate"}>
                        {p.payment_status || p.status}
                      </Pill>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
